import { useRef } from 'react'

import { ChevronDown } from 'lucide-react'

import { useFlipReorder } from '@/hooks/useFlipReorder'
import type { DeliveryDto } from '@/services/deliveryService'
import type { DayGroup } from '@/utils/scheduleTime'
import { PREVIOUS_DAYS_KEY } from '@/utils/scheduleTime'

import DeliveryTile from './DeliveryTile'
import './DeliveryTileGrid.css'

type Fold = {
  collapsed: boolean
  onToggle: () => void
}

type SectionProps = {
  day: DayGroup<DeliveryDto>
  now: Date
  onReschedule: (id: string, scheduledFor: string) => void
  onEdit: (delivery: DeliveryDto) => void
  /**
   * Present only on Previous day(s) — the one foldable section. Absent leaves
   * the heading as plain text, which is what every dated section wants.
   */
  fold?: Fold
}

/**
 * One day of the board. Owns its own FLIP scope on purpose: a reschedule
 * within the day slides tiles to their new order, while a move to another
 * day simply leaves this list and appears in the other — un-animated by
 * construction. That is the agreed behaviour (never animate what the eye
 * cannot follow) with no cross-section special-casing at all.
 */
function DaySection({ day, now, onReschedule, onEdit, fold }: SectionProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const collapsed = fold?.collapsed ?? false

  // Folding is part of the FLIP identity, not just the order. A fold empties
  // the container, so without it here the hook would keep its pre-fold
  // measurements and the first reschedule after expanding would animate from
  // stale positions -- or, worse, be judged "visibly moved" by the toast
  // logic when nothing slid at all.
  useFlipReorder(
    listRef,
    `${collapsed ? 'folded' : 'open'}:${day.deliveries
      .map((delivery) => delivery.id)
      .join(',')}`,
  )

  const listId = `day-list-${day.dayKey}`

  return (
    <section className="delivery-day" aria-labelledby={`day-${day.dayKey}`}>
      <h2 id={`day-${day.dayKey}`} className="delivery-day__heading">
        {fold ? (
          <button
            type="button"
            className="delivery-day__toggle"
            aria-expanded={!collapsed}
            aria-controls={listId}
            onClick={fold.onToggle}
          >
            <ChevronDown className="delivery-day__chevron" aria-hidden="true" />
            {day.label}
            {/* The count is what makes folding honest rather than hiding, so
                it is part of the control the user folds with. */}
            <span className="delivery-day__count">{day.deliveries.length}</span>
          </button>
        ) : (
          day.label
        )}
      </h2>

      {/* The container renders even when folded, so aria-controls always points
          at a real element -- only the tiles unmount. Unmounting rather than
          hiding keeps them out of the toast's visibility test, which measures
          whatever [data-flip-key] it can find in the board. */}
      <div className="delivery-day__list" id={listId} ref={listRef}>
        {collapsed
          ? null
          : day.deliveries.map((delivery) => (
              <DeliveryTile
                key={delivery.id}
                delivery={delivery}
                now={now}
                onReschedule={onReschedule}
                onEdit={onEdit}
              />
            ))}
      </div>
    </section>
  )
}

type Props = {
  days: DayGroup<DeliveryDto>[]
  /** The page's shared clock, passed to every tile. */
  now: Date
  onReschedule: (id: string, scheduledFor: string) => void
  onEdit: (delivery: DeliveryDto) => void
  /**
   * Fold state for Previous day(s), as rendered. The page derives it — a
   * filter or the Previous day(s) view can override the stored preference, so
   * this is not simply what the user last chose.
   */
  foldPreviousDays: boolean
  onTogglePreviousDays: () => void
}

/**
 * The board, as day sections. Each day is a real <section> with the date as
 * its heading rather than an interleaved label, so a screen-reader user
 * scrolling a week can tell which day they are in.
 */
function DeliveryTileGrid({
  days,
  now,
  onReschedule,
  onEdit,
  foldPreviousDays,
  onTogglePreviousDays,
}: Props) {
  return (
    <div className="delivery-schedule">
      {days.map((day) => (
        <DaySection
          key={day.dayKey}
          day={day}
          now={now}
          onReschedule={onReschedule}
          onEdit={onEdit}
          fold={
            day.dayKey === PREVIOUS_DAYS_KEY
              ? { collapsed: foldPreviousDays, onToggle: onTogglePreviousDays }
              : undefined
          }
        />
      ))}
    </div>
  )
}

export default DeliveryTileGrid
